import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import {
  AppField,
  FieldType,
  PopupFieldData,
  StageDefaultInfo,
  View,
} from "src/app/features/designer-studio/edit-pdf-doc/models/doc.model";
import {
  AddPopupField,
  RemovePopupField,
  UpdatePopupField,
  UpdatePopupStageData,
} from "src/app/features/designer-studio/edit-pdf-doc/state/actions/doc.actions";
import { getCurrentView } from "src/app/features/designer-studio/edit-pdf-doc/state/selectors/doc.selectors";
import { environment } from "src/environments/environment";
import { ComponentType, PopupAppFieldType } from "../../model/Component.model";

export interface MenuItemRes {
  elements: PopupMenuItem[];
}

export interface PopupMenuItem {
  cp_display: string;
  fontFamily: string;
  fontSize: string;
  hidden: false;
  icon: string;
  inner_text: string;
  mappedServiceId: number;
  name: string;
  name_for_server: string;
  parentId: number;
  style: string;
  textAlign: string;
  type: string;
}

const classNamesToIcons: { [key: string]: string } = {
  "glyphicon glyphicon-font": "TextNormal",
  "glyphicon glyphicon-text-color": "Link",
  "glyphicon glyphicon-text-background": "Square",
  "glyphicon glyphicon-modal-window": "Select",
  "glyphicon glyphicon-picture": "Image",
  "glyphicon glyphicon-ok-circle": "Success",
  "glyphicon glyphicon-check": "CheckAll",
  "glyphicon glyphicon-tasks": "ViewList",
  "glyphicon glyphicon-minus": "DrawLine",
};

@Injectable({
  providedIn: "root",
})
export class ScreenService {
  private screenData$ = new BehaviorSubject<View>(null);
  menuData$ = new BehaviorSubject<PopupMenuItem[]>([]);
  dragWarning$ = new BehaviorSubject<boolean>(false);
  private stageSize: DOMRect;

  componentDataChange$ = new BehaviorSubject(null);

  constructor(private httpClient: HttpClient, private store: Store) {
    this.fetchScreenData();
    this.fetchMenuData()
      .pipe(take(1))
      .subscribe((menuItems: PopupMenuItem[]) =>
        this.menuData$.next(menuItems)
      );
  }

  setStageSize(rect: DOMRect) {
    this.stageSize = rect;
  }

  getStageSize() {
    return this.stageSize;
  }

  updateFieldProperties(fieldData: PopupFieldData, send: boolean) {
    this.store.dispatch(new UpdatePopupField(structuredClone(fieldData)));
    if (send) {
      this.componentDataChange$.next(fieldData);
    }
  }

  updateStageProperties(stageData: StageDefaultInfo) {
    this.store.dispatch(new UpdatePopupStageData(structuredClone(stageData)));
  }

  addFieldToStore(screenField: PopupFieldData, appField: AppField) {
    this.store.dispatch(new AddPopupField(appField, screenField));
  }

  removeFieldFromStore(id: string) {
    this.store.dispatch(new RemovePopupField(id));
  }

  addScreenComponent(component) {
    const screenClone = structuredClone(this.screenData$.getValue());
    const appFieldData = this.generateAppFieldData(component);
    const lastTabOrder = Object.values(screenClone.appFields).reduce(
      (max, current) => {
        return current.tabOrder > max ? current.tabOrder : max;
      },
      0
    );

    appFieldData.tabOrder = lastTabOrder + 1;
    screenClone.appFields[component.id] = appFieldData as AppField;
    screenClone.popupDetails.data.push({
      ...component,
      tabOrder: lastTabOrder + 1,
    });
    this.setScreenData(screenClone);
    this.addFieldToStore(component, appFieldData);
  }

  generateAppFieldData(detail: PopupFieldData): AppField {
    if (
      !detail.type ||
      typeof detail.type !== "string" ||
      !PopupAppFieldType[detail.type.toUpperCase()]
    ) {
      throw new Error(
        "Proper type not passed to generateAppFieldData" + detail.type
      );
    }

    return {
      name: detail.id,
      displayName: detail.fieldDisplayName,
      value: "",
      imageValue: "",
      printValue: "",
      derived: false,
      valueExpression: "string",
      type: PopupAppFieldType[detail.type.toUpperCase()],
      pdfFieldName: detail.id,
      pdfFieldType: "",
      readOnly: false,
      invisible: false,
      maskOnPrint: false,
      maskOnImaging: false,
      hideOnPrint: false,
      fieldDataType: {
        type:
          detail.type === "image"
            ? FieldType.IMAGE
            : FieldType.ALPHA_NUMERIC_SPECIALCHARS,
        format: "",
        required: false,
        maxLength: -1,
        range: {
          min: "",
          max: "",
        },
        validator: {
          script: "",
          primary: "",
        },
      },
      fieldTriggers: {},
      tabOrder: 0,
      dependentFields: [],
      fonts: {},
      helpLtrChoice: "",
      helpLtrFile: "",
      options: [],
      signatureSequenceNumber: "",
      signInitial: false,
      signatureDateField: "",
      signatureData: "",
      signerId: "",
      defaultValue: "",
      defaultListValue: [],
      filter: {
        code: "",
        expressions: [],
      },
      signerList: [],
      mask: {},
    };
  }

  getScreenData() {
    return this.screenData$.asObservable().pipe(
      map((view) => {
        const dataFields = [];

        view.popupDetails.data.forEach((field) => {
          if (view.appFields[field.id]) {
            dataFields.push({
              ...view.appFields[field.id],
              ...field,
            });
          }
        });
        view.popupDetails.data = dataFields;
        return view;
      })
    );
  }

  private fetchMenuData(): Observable<PopupMenuItem[]> {
    let path = environment.staticDataUrl;
    if (path[path.length - 1] === "/") {
      path = path + "admin/powerScreens/data/menu-data.json";
    } else {
      path = path + "/admin/powerScreens/data/menu-data.json";
    }
    return this.httpClient.get(path).pipe(
      map((res: MenuItemRes) =>
        res.elements.map((res: PopupMenuItem) => {
          return { ...res, icon: this.getRUIIconNameFromClass(res.icon) };
        })
      )
    );
  }

  private getRUIIconNameFromClass(classString: string): string {
    if (
      !classString ||
      classString === "" ||
      !Object.keys(classNamesToIcons).includes(classString)
    )
      return "Unknown";

    return classNamesToIcons[classString] || "Unknown";
  }

  private fetchScreenData() {
    this.store
      .select(getCurrentView)
      .pipe(filter((view) => !!view && view.viewType === "POPUP"))
      .subscribe((view) => {
        this.setScreenData(view);
      });
  }

  private setScreenData(data) {
    this.screenData$.next(structuredClone(data));
  }

  emToPx(emValue: string): number {
    const toNumeric = parseFloat(emValue.match(/([\d.]+)/)[1]);
    const fontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const pxValue = Math.floor(toNumeric * fontSize);
    return pxValue;
  }
}
